import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Receipt, Plus, Trash2, Download, Send } from "lucide-react";
import html2canvas from "html2canvas";

export default function InvoiceGenerator() {
  const invoiceRef = useRef(null);
  const [invoice, setInvoice] = useState({
    invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
    date: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
    from: { name: "", email: "", address: "" },
    to: { name: "", email: "", address: "" },
    items: [{ description: "", quantity: 1, rate: 0 }],
    notes: ""
  });

  const addItem = () => setInvoice(p => ({ ...p, items: [...p.items, { description: "", quantity: 1, rate: 0 }] }));
  const removeItem = (i) => setInvoice(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, value) => {
    const items = [...invoice.items];
    items[i][field] = field === 'quantity' || field === 'rate' ? parseFloat(value) || 0 : value;
    setInvoice(p => ({ ...p, items }));
  };

  const subtotal = invoice.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  const tax = subtotal * 0; // No tax for simplicity
  const total = subtotal + tax;

  const downloadInvoice = async () => {
    if (!invoiceRef.current) return;
    const canvas = await html2canvas(invoiceRef.current, { scale: 2 });
    const link = document.createElement('a');
    link.download = `${invoice.invoiceNumber}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <div className="min-h-screen" style={{ background: '#fffdf5' }}>
      <nav className="border-b border-[#e5e5e5] bg-white/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#666] hover:text-[#121212]">
            <ArrowLeft className="w-4 h-4" /> Back to FinAI
          </Link>
          <Button onClick={downloadInvoice} className="h-10 rounded-full bg-[#121212]">
            <Download className="w-4 h-4 mr-2" /> Download Invoice
          </Button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#ffcc00]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Receipt className="w-8 h-8 text-[#121212]" />
          </div>
          <h1 className="text-4xl font-bold text-[#121212] mb-4">Free Invoice Generator</h1>
          <p className="text-lg text-[#666]">Create professional invoices in seconds—no signup required.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Editor */}
          <div className="space-y-6">
            <Card className="border-2 border-[#e5e5e5]">
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Invoice #</Label><Input value={invoice.invoiceNumber} onChange={e => setInvoice({...invoice, invoiceNumber: e.target.value})} /></div>
                  <div><Label>Date</Label><Input type="date" value={invoice.date} onChange={e => setInvoice({...invoice, date: e.target.value})} /></div>
                  <div><Label>Due Date</Label><Input type="date" value={invoice.dueDate} onChange={e => setInvoice({...invoice, dueDate: e.target.value})} /></div>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border-2 border-[#e5e5e5]">
                <CardContent className="p-6 space-y-3">
                  <h3 className="font-semibold">From</h3>
                  <Input placeholder="Your Name/Company" value={invoice.from.name} onChange={e => setInvoice({...invoice, from: {...invoice.from, name: e.target.value}})} />
                  <Input placeholder="Email" value={invoice.from.email} onChange={e => setInvoice({...invoice, from: {...invoice.from, email: e.target.value}})} />
                  <Textarea placeholder="Address" rows={2} value={invoice.from.address} onChange={e => setInvoice({...invoice, from: {...invoice.from, address: e.target.value}})} />
                </CardContent>
              </Card>
              <Card className="border-2 border-[#e5e5e5]">
                <CardContent className="p-6 space-y-3">
                  <h3 className="font-semibold">To</h3>
                  <Input placeholder="Client Name/Company" value={invoice.to.name} onChange={e => setInvoice({...invoice, to: {...invoice.to, name: e.target.value}})} />
                  <Input placeholder="Email" value={invoice.to.email} onChange={e => setInvoice({...invoice, to: {...invoice.to, email: e.target.value}})} />
                  <Textarea placeholder="Address" rows={2} value={invoice.to.address} onChange={e => setInvoice({...invoice, to: {...invoice.to, address: e.target.value}})} />
                </CardContent>
              </Card>
            </div>

            <Card className="border-2 border-[#e5e5e5]">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-semibold">Line Items</h3>
                {invoice.items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-6" placeholder="Description" value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} />
                    <Input className="col-span-2" type="number" placeholder="Qty" value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} />
                    <Input className="col-span-3" type="number" placeholder="Rate" value={item.rate} onChange={e => updateItem(i, 'rate', e.target.value)} />
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(i)} disabled={invoice.items.length === 1}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" onClick={addItem} className="w-full rounded-full">
                  <Plus className="w-4 h-4 mr-2" /> Add Line Item
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#e5e5e5]">
              <CardContent className="p-6">
                <Label>Notes</Label>
                <Textarea placeholder="Payment terms, thank you message, etc." rows={3} value={invoice.notes} onChange={e => setInvoice({...invoice, notes: e.target.value})} />
              </CardContent>
            </Card>
          </div>

          {/* Preview */}
          <div>
            <div ref={invoiceRef} className="bg-white rounded-2xl border-2 border-[#e5e5e5] p-8 shadow-lg">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h2 className="text-3xl font-bold text-[#121212]">INVOICE</h2>
                  <p className="text-[#666]">{invoice.invoiceNumber}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#666]">Date: {invoice.date}</p>
                  <p className="text-sm text-[#666]">Due: {invoice.dueDate}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="text-xs text-[#666] uppercase tracking-wider mb-2">From</p>
                  <p className="font-semibold">{invoice.from.name || 'Your Name'}</p>
                  <p className="text-sm text-[#666]">{invoice.from.email}</p>
                  <p className="text-sm text-[#666] whitespace-pre-line">{invoice.from.address}</p>
                </div>
                <div>
                  <p className="text-xs text-[#666] uppercase tracking-wider mb-2">Bill To</p>
                  <p className="font-semibold">{invoice.to.name || 'Client Name'}</p>
                  <p className="text-sm text-[#666]">{invoice.to.email}</p>
                  <p className="text-sm text-[#666] whitespace-pre-line">{invoice.to.address}</p>
                </div>
              </div>

              <table className="w-full mb-8">
                <thead>
                  <tr className="border-b-2 border-[#e5e5e5]">
                    <th className="text-left py-3 text-sm text-[#666]">Description</th>
                    <th className="text-right py-3 text-sm text-[#666]">Qty</th>
                    <th className="text-right py-3 text-sm text-[#666]">Rate</th>
                    <th className="text-right py-3 text-sm text-[#666]">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, i) => (
                    <tr key={i} className="border-b border-[#e5e5e5]">
                      <td className="py-3">{item.description || 'Item'}</td>
                      <td className="text-right py-3">{item.quantity}</td>
                      <td className="text-right py-3">${item.rate.toFixed(2)}</td>
                      <td className="text-right py-3 font-medium">${(item.quantity * item.rate).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex justify-end">
                <div className="w-64">
                  <div className="flex justify-between py-2">
                    <span className="text-[#666]">Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-t-2 border-[#121212] font-bold text-lg">
                    <span>Total</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {invoice.notes && (
                <div className="mt-8 pt-4 border-t border-[#e5e5e5]">
                  <p className="text-xs text-[#666] uppercase tracking-wider mb-2">Notes</p>
                  <p className="text-sm text-[#666]">{invoice.notes}</p>
                </div>
              )}

              <div className="mt-8 text-center text-xs text-[#999]">
                Generated with FinAI Invoice Generator
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
